/**
 * Utilities for extracting keyframe information from Lottie layers
 */

// Unified row height for tree structure (both layers and properties)
export const TREE_ROW_HEIGHT = 28;

// Track height configuration for timeline display
export const TRACK_HEIGHT_CONFIG = {
  BASE_HEIGHT: 48,
  COLLAPSED_HEIGHT: 28,
  LAYER_BAR_HEIGHT: 24,
  KEYFRAME_ROW_HEIGHT: 20,
} as const;

// Ordered list of property types for consistent display
export const DISPLAY_PROPERTY_TYPES: readonly KeyframePropertyType[] = [
  "position",
  "scale",
  "rotation",
  "opacity",
  "anchor",
  "shape",
  "text",
] as const;

export type TextAnimationGrouping = "characters" | "words" | "lines";
export type TextAnimationPhase = "in" | "out" | "both";

export const TEXT_ANIMATION_GROUPINGS: readonly TextAnimationGrouping[] = [
  "characters",
  "words",
  "lines",
] as const;

export const TEXT_ANIMATION_PHASES: readonly TextAnimationPhase[] = [
  "in",
  "out",
  "both",
] as const;

export type KeyframePropertyType =
  | "position"
  | "scale"
  | "rotation"
  | "opacity"
  | "anchor"
  | "skew"
  | "skewAxis"
  | "shape"
  | "text"
  | "other";

type KeyframeBezierHandle = {
  x: number | number[];
  y: number | number[];
};

interface KeyframeData {
  t: number;
  s?: unknown;
  e?: unknown;
  i?: KeyframeBezierHandle;
  o?: KeyframeBezierHandle;
  h?: 0 | 1;
}

interface AnimatedProperty {
  a?: number;
  k?: KeyframeData[] | number | number[];
}

interface TextAnimatorRangeSelector {
  a?: AnimatedProperty;
  xe?: AnimatedProperty;
  ne?: AnimatedProperty;
  o?: AnimatedProperty;
  sm?: AnimatedProperty;
  s?: AnimatedProperty;
  e?: AnimatedProperty;
  b?: number | AnimatedProperty;
}

interface TextAnimatorStyle {
  p?: AnimatedProperty | { x?: AnimatedProperty; y?: AnimatedProperty };
  s?: AnimatedProperty;
  r?: AnimatedProperty;
  o?: AnimatedProperty;
  a?: AnimatedProperty;
  sk?: AnimatedProperty;
  sa?: AnimatedProperty;
  t?: AnimatedProperty;
  bl?: AnimatedProperty;
  ls?: AnimatedProperty;
  sw?: AnimatedProperty;
  sh?: AnimatedProperty;
  ss?: AnimatedProperty;
  sb?: AnimatedProperty;
  so?: AnimatedProperty;
  fh?: AnimatedProperty;
  fs?: AnimatedProperty;
  fb?: AnimatedProperty;
  fo?: AnimatedProperty;
  fc?: AnimatedProperty;
  sc?: AnimatedProperty;
}

interface TextAnimator {
  nm?: string;
  s?: TextAnimatorRangeSelector;
  a?: TextAnimatorStyle;
}

export interface TextAnimationMetadata {
  animatorIndex: number;
  rootPath: string;
  displayName: string;
  source: "preset" | "custom";
  presetName?: string;
  phase?: TextAnimationPhase;
  phasePath?: string;
  grouping?: TextAnimationGrouping;
  groupingPath?: string;
  amount?: number;
  amountPath?: string;
}

type KeyframeRowDescriptor = {
  id: string;
  name: string;
  order: number;
  textAnimation?: TextAnimationMetadata;
};

export interface KeyframeInfo {
  t: number;
  path: string;
  propertyType: KeyframePropertyType;
  propertyName: string;
  keyframeIndex: number;
  originalKeyframe: KeyframeData;
  splitDimension?: "x" | "y";
  rowId: string;
  rowName: string;
  rowOrder: number;
  textAnimation?: TextAnimationMetadata;
}

export interface KeyframeRow {
  id: string;
  propertyType: KeyframePropertyType;
  propertyName: string;
  keyframes: KeyframeInfo[];
  textAnimation?: TextAnimationMetadata;
  order: number;
}

interface LayerTransform {
  p?: AnimatedProperty | { x?: AnimatedProperty; y?: AnimatedProperty };
  s?: AnimatedProperty;
  r?: AnimatedProperty;
  o?: AnimatedProperty;
  a?: AnimatedProperty;
  sk?: AnimatedProperty;
  sa?: AnimatedProperty;
}

interface ShapeStyleItem {
  ty?: string;
  nm?: string;
  ks?: LayerTransform;
  it?: ShapeStyleItem[];
  c?: AnimatedProperty;
  o?: AnimatedProperty;
  w?: AnimatedProperty;
}

interface LayerWithKeyframes {
  ks?: LayerTransform;
  st?: number;
  shapes?: ShapeStyleItem[];
  ef?: unknown;
  t?: {
    d?: AnimatedProperty;
    a?: TextAnimator[];
  };
}

const KEYFRAME_PATH_INDEX_REGEX = /\[(\d+)\]/g;
const BLINGS_TEXT_ANIMATOR_PREFIX = "__blingsMotion:";
const TEXT_DOCUMENT_PROPERTY_PATH = "t.d";

const TEXT_GROUPING_BASE_TO_VALUE = {
  1: "characters",
  3: "words",
  4: "lines",
} as const satisfies Record<number, TextAnimationGrouping>;

const TEXT_GROUPING_VALUE_TO_BASE: Record<TextAnimationGrouping, number> = {
  characters: 1,
  words: 3,
  lines: 4,
};

const TEXT_SELECTOR_PROPERTY_LABELS = {
  a: "Amount",
  xe: "Ease High",
  ne: "Ease Low",
  o: "Offset",
  sm: "Smoothness",
  s: "Start",
  e: "End",
} as const;

const TEXT_STYLE_PROPERTY_LABELS = {
  t: "Tracking",
  bl: "Blur",
  ls: "Line Spacing",
  sw: "Stroke Width",
  sh: "Stroke Hue",
  ss: "Stroke Saturation",
  sb: "Stroke Brightness",
  so: "Stroke Opacity",
  fh: "Fill Hue",
  fs: "Fill Saturation",
  fb: "Fill Brightness",
  fo: "Fill Opacity",
  fc: "Fill Color",
  sc: "Stroke Color",
} as const;

const SHAPE_STYLE_PROPERTY_LABELS = {
  fillColor: "Fill Color",
  fillOpacity: "Fill Opacity",
  strokeColor: "Stroke Color",
  strokeWidth: "Stroke Width",
  strokeOpacity: "Stroke Opacity",
} as const;

export const normalizeKeyframePath = (path: string): string =>
  path.replace(KEYFRAME_PATH_INDEX_REGEX, ".$1");

export const getKeyframePropertyPath = (path: string): string =>
  normalizeKeyframePath(path).replace(/\.k\.\d+\.t$/, "");

export function formatTextAnimationPresetName(presetName: string): string {
  return presetName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function textAnimationGroupingToBase(
  grouping: TextAnimationGrouping
): number {
  return TEXT_GROUPING_VALUE_TO_BASE[grouping];
}

function cloneKeyframeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return [...value];
  }

  if (value && typeof value === "object") {
    return JSON.parse(JSON.stringify(value));
  }

  return value;
}

function cloneHandle(handle: KeyframeBezierHandle): KeyframeBezierHandle {
  return {
    x: Array.isArray(handle.x) ? [...handle.x] : handle.x,
    y: Array.isArray(handle.y) ? [...handle.y] : handle.y,
  };
}

function cloneKeyframeData(keyframe: KeyframeData): KeyframeData {
  return {
    t: keyframe.t,
    ...(keyframe.s !== undefined ? { s: cloneKeyframeValue(keyframe.s) } : {}),
    ...(keyframe.e !== undefined ? { e: cloneKeyframeValue(keyframe.e) } : {}),
    ...(keyframe.i ? { i: cloneHandle(keyframe.i) } : {}),
    ...(keyframe.o ? { o: cloneHandle(keyframe.o) } : {}),
    ...(keyframe.h !== undefined ? { h: keyframe.h } : {}),
  };
}

function createRowDescriptor(
  id: string,
  name: string,
  order: number,
  textAnimation?: TextAnimationMetadata
): KeyframeRowDescriptor {
  return { id, name, order, textAnimation };
}

function isTimedKeyframe(value: unknown): value is KeyframeData {
  return (
    value !== null &&
    typeof value === "object" &&
    "t" in value &&
    typeof (value as { t?: unknown }).t === "number"
  );
}

function hasTimedKeyframeList(
  prop: AnimatedProperty | undefined
): prop is AnimatedProperty & { k: unknown[] } {
  return Boolean(prop && Array.isArray(prop.k) && prop.k.some(isTimedKeyframe));
}

function extractKeyframeTimes(prop: AnimatedProperty | undefined): number[] {
  if (!hasTimedKeyframeList(prop)) {
    return [];
  }

  return prop.k.reduce<number[]>((times, keyframe) => {
    if (isTimedKeyframe(keyframe)) {
      times.push(keyframe.t);
    }

    return times;
  }, []);
}

function extractPositionKeyframeTimes(
  prop: AnimatedProperty | { x?: AnimatedProperty; y?: AnimatedProperty } | undefined
): number[] {
  if (!prop) return [];

  if ("x" in prop || "y" in prop) {
    const splitProp = prop as { x?: AnimatedProperty; y?: AnimatedProperty };
    return [
      ...extractKeyframeTimes(splitProp.x),
      ...extractKeyframeTimes(splitProp.y),
    ];
  }

  return extractKeyframeTimes(prop as AnimatedProperty);
}

function extractTransformKeyframeTimes(ks: LayerTransform | undefined): number[] {
  if (!ks) return [];

  return [
    ...extractPositionKeyframeTimes(ks.p),
    ...extractKeyframeTimes(ks.s),
    ...extractKeyframeTimes(ks.r),
    ...extractKeyframeTimes(ks.o),
    ...extractKeyframeTimes(ks.a),
    ...extractKeyframeTimes(ks.sk),
    ...extractKeyframeTimes(ks.sa),
  ];
}

function extractKeyframeInfo(
  prop: AnimatedProperty | undefined,
  basePath: string,
  propertyType: KeyframePropertyType,
  propertyName: string,
  row: KeyframeRowDescriptor
): KeyframeInfo[] {
  if (!hasTimedKeyframeList(prop)) {
    return [];
  }

  return prop.k
    .map((keyframe, index): KeyframeInfo | null => {
      if (!isTimedKeyframe(keyframe)) return null;

      return {
        t: keyframe.t,
        path: normalizeKeyframePath(`${basePath}.k[${index}].t`),
        propertyType,
        propertyName,
        keyframeIndex: index,
        originalKeyframe: cloneKeyframeData(keyframe),
        rowId: row.id,
        rowName: row.name,
        rowOrder: row.order,
        textAnimation: row.textAnimation,
      };
    })
    .filter((keyframe): keyframe is KeyframeInfo => keyframe !== null);
}

function extractSplitPositionKeyframeInfo(
  prop: { x?: AnimatedProperty; y?: AnimatedProperty },
  basePath: string,
  row: KeyframeRowDescriptor
): KeyframeInfo[] {
  const keyframes: KeyframeInfo[] = [];

  if (prop.x) {
    keyframes.push(
      ...extractKeyframeInfo(prop.x, `${basePath}.x`, "position", "Position X", row).map(
        (keyframe) => ({ ...keyframe, splitDimension: "x" as const })
      )
    );
  }

  if (prop.y) {
    keyframes.push(
      ...extractKeyframeInfo(prop.y, `${basePath}.y`, "position", "Position Y", row).map(
        (keyframe) => ({ ...keyframe, splitDimension: "y" as const })
      )
    );
  }

  return keyframes;
}

function extractTransformKeyframeInfo(
  ks: LayerTransform | undefined,
  basePath: string,
  orderByPropertyType: Partial<Record<KeyframePropertyType, number>>
): KeyframeInfo[] {
  if (!ks) return [];

  const keyframes: KeyframeInfo[] = [];

  const positionRow = createRowDescriptor(
    "position",
    "Position",
    orderByPropertyType.position ?? 0
  );
  const scaleRow = createRowDescriptor("scale", "Scale", orderByPropertyType.scale ?? 1);
  const rotationRow = createRowDescriptor(
    "rotation",
    "Rotation",
    orderByPropertyType.rotation ?? 2
  );
  const opacityRow = createRowDescriptor(
    "opacity",
    "Opacity",
    orderByPropertyType.opacity ?? 3
  );
  const anchorRow = createRowDescriptor("anchor", "Anchor", orderByPropertyType.anchor ?? 4);
  const skewRow = createRowDescriptor("skew", "Skew", orderByPropertyType.skew ?? 5);
  const skewAxisRow = createRowDescriptor(
    "skewAxis",
    "Skew Axis",
    orderByPropertyType.skewAxis ?? 6
  );

  if (ks.p) {
    if ("x" in ks.p || "y" in ks.p) {
      keyframes.push(
        ...extractSplitPositionKeyframeInfo(
          ks.p as { x?: AnimatedProperty; y?: AnimatedProperty },
          `${basePath}.p`,
          positionRow
        )
      );
    } else {
      keyframes.push(
        ...extractKeyframeInfo(
          ks.p as AnimatedProperty,
          `${basePath}.p`,
          "position",
          "Position",
          positionRow
        )
      );
    }
  }

  keyframes.push(...extractKeyframeInfo(ks.s, `${basePath}.s`, "scale", "Scale", scaleRow));
  keyframes.push(
    ...extractKeyframeInfo(ks.r, `${basePath}.r`, "rotation", "Rotation", rotationRow)
  );
  keyframes.push(
    ...extractKeyframeInfo(ks.o, `${basePath}.o`, "opacity", "Opacity", opacityRow)
  );
  keyframes.push(
    ...extractKeyframeInfo(ks.a, `${basePath}.a`, "anchor", "Anchor Point", anchorRow)
  );
  keyframes.push(...extractKeyframeInfo(ks.sk, `${basePath}.sk`, "skew", "Skew", skewRow));
  keyframes.push(
    ...extractKeyframeInfo(ks.sa, `${basePath}.sa`, "skewAxis", "Skew Axis", skewAxisRow)
  );

  return keyframes;
}

function buildShapeStyleRowName(baseName: string, label: string): string {
  const normalizedBaseName = baseName.toLowerCase();
  if (
    normalizedBaseName.endsWith(" fill") &&
    label.toLowerCase().startsWith("fill ")
  ) {
    return `${baseName} ${label.slice(5)}`;
  }

  if (
    normalizedBaseName.endsWith(" stroke") &&
    label.toLowerCase().startsWith("stroke ")
  ) {
    return `${baseName} ${label.slice(7)}`;
  }

  return baseName.toLowerCase() === label.toLowerCase()
    ? baseName
    : `${baseName} ${label}`;
}

function extractShapeStyleKeyframeInfo(
  items: ShapeStyleItem[] | undefined,
  basePath: string,
  orderRef: { value: number }
): KeyframeInfo[] {
  if (!items?.length) {
    return [];
  }

  const keyframes: KeyframeInfo[] = [];

  items.forEach((item, index) => {
    const itemPath = `${basePath}[${index}]`;
    const itemName =
      item.nm ??
      (item.ty === "fl"
        ? "Fill"
        : item.ty === "st"
        ? "Stroke"
        : `Shape ${index + 1}`);

    if (item.ty === "fl") {
      const fillColorLabel = buildShapeStyleRowName(
        itemName,
        SHAPE_STYLE_PROPERTY_LABELS.fillColor
      );
      const fillOpacityLabel = buildShapeStyleRowName(
        itemName,
        SHAPE_STYLE_PROPERTY_LABELS.fillOpacity
      );
      keyframes.push(
        ...extractKeyframeInfo(
          item.c,
          `${itemPath}.c`,
          "shape",
          fillColorLabel,
          createRowDescriptor(
            `shape-style-${normalizeKeyframePath(`${itemPath}.c`)}`,
            fillColorLabel,
            orderRef.value++
          )
        )
      );
      keyframes.push(
        ...extractKeyframeInfo(
          item.o,
          `${itemPath}.o`,
          "shape",
          fillOpacityLabel,
          createRowDescriptor(
            `shape-style-${normalizeKeyframePath(`${itemPath}.o`)}`,
            fillOpacityLabel,
            orderRef.value++
          )
        )
      );
    }

    if (item.ty === "st") {
      const strokeColorLabel = buildShapeStyleRowName(
        itemName,
        SHAPE_STYLE_PROPERTY_LABELS.strokeColor
      );
      const strokeWidthLabel = buildShapeStyleRowName(
        itemName,
        SHAPE_STYLE_PROPERTY_LABELS.strokeWidth
      );
      const strokeOpacityLabel = buildShapeStyleRowName(
        itemName,
        SHAPE_STYLE_PROPERTY_LABELS.strokeOpacity
      );
      keyframes.push(
        ...extractKeyframeInfo(
          item.c,
          `${itemPath}.c`,
          "shape",
          strokeColorLabel,
          createRowDescriptor(
            `shape-style-${normalizeKeyframePath(`${itemPath}.c`)}`,
            strokeColorLabel,
            orderRef.value++
          )
        )
      );
      keyframes.push(
        ...extractKeyframeInfo(
          item.w,
          `${itemPath}.w`,
          "shape",
          strokeWidthLabel,
          createRowDescriptor(
            `shape-style-${normalizeKeyframePath(`${itemPath}.w`)}`,
            strokeWidthLabel,
            orderRef.value++
          )
        )
      );
      keyframes.push(
        ...extractKeyframeInfo(
          item.o,
          `${itemPath}.o`,
          "shape",
          strokeOpacityLabel,
          createRowDescriptor(
            `shape-style-${normalizeKeyframePath(`${itemPath}.o`)}`,
            strokeOpacityLabel,
            orderRef.value++
          )
        )
      );
    }

    keyframes.push(
      ...extractShapeStyleKeyframeInfo(item.it, `${itemPath}.it`, orderRef)
    );
  });

  return keyframes;
}

function extractShapeStyleKeyframeTimes(items: ShapeStyleItem[] | undefined): number[] {
  if (!items?.length) {
    return [];
  }

  return items.flatMap((item) => [
    ...extractKeyframeTimes(item.ty === "fl" || item.ty === "st" ? item.c : undefined),
    ...extractKeyframeTimes(item.ty === "fl" || item.ty === "st" ? item.o : undefined),
    ...extractKeyframeTimes(item.ty === "st" ? item.w : undefined),
    ...extractShapeStyleKeyframeTimes(item.it),
  ]);
}

function getAnimatedNumericPropertyValue(prop: unknown): number | undefined {
  if (typeof prop === "number" && Number.isFinite(prop)) {
    return prop;
  }

  if (
    prop &&
    typeof prop === "object" &&
    "a" in (prop as Record<string, unknown>) &&
    "k" in (prop as Record<string, unknown>) &&
    (prop as AnimatedProperty).a === 0 &&
    typeof (prop as AnimatedProperty).k === "number"
  ) {
    return (prop as { k: number }).k;
  }

  return undefined;
}

export function detectTextAnimationGrouping(value: unknown): TextAnimationGrouping | undefined {
  const base = getAnimatedNumericPropertyValue(value);
  if (base == null) {
    return undefined;
  }

  return TEXT_GROUPING_BASE_TO_VALUE[base];
}

function getKeyframeScalarValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (
    Array.isArray(value) &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0])
  ) {
    return value[0];
  }

  return undefined;
}

export function detectTextAnimationPhase(value: unknown): TextAnimationPhase | undefined {
  if (
    !value ||
    typeof value !== "object" ||
    !hasTimedKeyframeList(value as AnimatedProperty)
  ) {
    return undefined;
  }

  let hasEnter = false;
  let hasExit = false;

  const keyframes = (value as AnimatedProperty & { k: unknown[] }).k;

  keyframes.forEach((keyframe) => {
    if (!isTimedKeyframe(keyframe)) {
      return;
    }

    const fromValue = getKeyframeScalarValue((keyframe as KeyframeData).s);
    const toValue = getKeyframeScalarValue((keyframe as KeyframeData).e);
    if (fromValue == null || toValue == null || fromValue === toValue) {
      return;
    }

    if (fromValue < toValue) {
      hasEnter = true;
      return;
    }

    hasExit = true;
  });

  if (hasEnter && hasExit) {
    return "both";
  }

  if (hasEnter) {
    return "in";
  }

  if (hasExit) {
    return "out";
  }

  return undefined;
}

function getTextAnimationMetadata(
  animator: TextAnimator,
  animatorIndex: number
): TextAnimationMetadata {
  const presetName = animator.nm?.startsWith(BLINGS_TEXT_ANIMATOR_PREFIX)
    ? animator.nm.slice(BLINGS_TEXT_ANIMATOR_PREFIX.length)
    : undefined;
  const source = presetName ? "preset" : "custom";
  const displayName = presetName
    ? formatTextAnimationPresetName(presetName)
    : animator.nm
    ? formatTextAnimationPresetName(animator.nm)
    : "Text animation";
  const detectedPhase = animator.s?.o
    ? detectTextAnimationPhase(animator.s.o)
    : undefined;

  return {
    animatorIndex,
    rootPath: normalizeKeyframePath(`t.a[${animatorIndex}]`),
    displayName,
    source,
    ...(presetName ? { presetName } : {}),
    ...(detectedPhase
      ? {
          phase: detectedPhase,
          phasePath: normalizeKeyframePath(`t.a[${animatorIndex}].s.o`),
        }
      : {}),
    ...(animator.s?.b
      ? {
          grouping: detectTextAnimationGrouping(animator.s.b),
          groupingPath: normalizeKeyframePath(`t.a[${animatorIndex}].s.b`),
        }
      : {}),
    ...(animator.s?.a
      ? {
          amount: getAnimatedNumericPropertyValue(animator.s.a),
          amountPath: normalizeKeyframePath(`t.a[${animatorIndex}].s.a`),
        }
      : {}),
  };
}

function extractTextAnimatorSelectorKeyframeInfo(
  selector: TextAnimatorRangeSelector | undefined,
  basePath: string,
  row: KeyframeRowDescriptor
): KeyframeInfo[] {
  if (!selector) return [];

  const keyframes: KeyframeInfo[] = [];

  (
    Object.entries(TEXT_SELECTOR_PROPERTY_LABELS) as Array<
      [keyof typeof TEXT_SELECTOR_PROPERTY_LABELS, string]
    >
  ).forEach(([propertyKey, label]) => {
    keyframes.push(
      ...extractKeyframeInfo(
        selector[propertyKey],
        `${basePath}.${propertyKey}`,
        "text",
        `${row.name} ${label}`,
        row
      )
    );
  });

  return keyframes;
}

function extractTextAnimatorStyleKeyframeInfo(
  style: TextAnimatorStyle | undefined,
  basePath: string,
  row: KeyframeRowDescriptor
): KeyframeInfo[] {
  if (!style) return [];

  const transformKeyframes = extractTransformKeyframeInfo(style, basePath, {});
  const keyframes: KeyframeInfo[] = transformKeyframes.map((keyframe) => ({
    ...keyframe,
    propertyType: "text" as KeyframePropertyType,
    propertyName: `${row.name} ${keyframe.propertyName}`,
    rowId: row.id,
    rowName: row.name,
    rowOrder: row.order,
    textAnimation: row.textAnimation,
  }));

  (
    Object.entries(TEXT_STYLE_PROPERTY_LABELS) as Array<
      [keyof typeof TEXT_STYLE_PROPERTY_LABELS, string]
    >
  ).forEach(([propertyKey, label]) => {
    keyframes.push(
      ...extractKeyframeInfo(
        style[propertyKey],
        `${basePath}.${propertyKey}`,
        "text",
        row.name.toLowerCase() === label.toLowerCase()
          ? row.name
          : `${row.name} ${label}`,
        row
      )
    );
  });

  return keyframes;
}

function extractTextAnimatorKeyframeInfo(
  animator: TextAnimator,
  animatorIndex: number,
  order: number
): KeyframeInfo[] {
  const metadata = getTextAnimationMetadata(animator, animatorIndex);
  const row = createRowDescriptor(
    `text-animator-${animatorIndex}`,
    metadata.displayName,
    order,
    metadata
  );

  return [
    ...extractTextAnimatorSelectorKeyframeInfo(animator.s, `t.a[${animatorIndex}].s`, row),
    ...extractTextAnimatorStyleKeyframeInfo(animator.a, `t.a[${animatorIndex}].a`, row),
  ];
}

function extractTextAnimatorKeyframeTimes(animator: TextAnimator): number[] {
  const selectorTimes = animator.s
    ? [
        ...extractKeyframeTimes(animator.s.a),
        ...extractKeyframeTimes(animator.s.xe),
        ...extractKeyframeTimes(animator.s.ne),
        ...extractKeyframeTimes(animator.s.o),
        ...extractKeyframeTimes(animator.s.sm),
        ...extractKeyframeTimes(animator.s.s),
        ...extractKeyframeTimes(animator.s.e),
      ]
    : [];

  const styleTimes = animator.a
    ? [
        ...extractTransformKeyframeTimes(animator.a),
        ...extractKeyframeTimes(animator.a.t),
        ...extractKeyframeTimes(animator.a.bl),
        ...extractKeyframeTimes(animator.a.ls),
        ...extractKeyframeTimes(animator.a.sw),
        ...extractKeyframeTimes(animator.a.sh),
        ...extractKeyframeTimes(animator.a.ss),
        ...extractKeyframeTimes(animator.a.sb),
        ...extractKeyframeTimes(animator.a.so),
        ...extractKeyframeTimes(animator.a.fh),
        ...extractKeyframeTimes(animator.a.fs),
        ...extractKeyframeTimes(animator.a.fb),
        ...extractKeyframeTimes(animator.a.fo),
        ...extractKeyframeTimes(animator.a.fc),
        ...extractKeyframeTimes(animator.a.sc),
      ]
    : [];

  return [...selectorTimes, ...styleTimes];
}

function extractNamedValueKeyframeInfo(
  value: unknown,
  basePath: string,
  orderRef: { value: number }
): KeyframeInfo[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) =>
      extractNamedValueKeyframeInfo(entry, `${basePath}[${index}]`, orderRef)
    );
  }

  if (!value || typeof value !== "object") {
    return [];
  }

  const record = value as Record<string, unknown>;
  const keyframes: KeyframeInfo[] = [];

  if (typeof record.nm === "string" && isAnimatedProperty(record.v)) {
    const propertyName = formatTextAnimationPresetName(record.nm);
    keyframes.push(
      ...extractKeyframeInfo(
        record.v,
        `${basePath}.v`,
        "other",
        propertyName,
        createRowDescriptor(
          `named-value-${normalizeKeyframePath(`${basePath}.v`)}`,
          propertyName,
          orderRef.value++
        )
      )
    );
  }

  Object.entries(record).forEach(([key, entry]) => {
    keyframes.push(
      ...extractNamedValueKeyframeInfo(
        entry,
        basePath ? `${basePath}.${key}` : key,
        orderRef
      )
    );
  });

  return keyframes;
}

function extractNamedValueKeyframeTimes(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractNamedValueKeyframeTimes(entry));
  }

  if (!value || typeof value !== "object") {
    return [];
  }

  const record = value as Record<string, unknown>;
  const directTimes =
    typeof record.nm === "string" && isAnimatedProperty(record.v)
      ? extractKeyframeTimes(record.v)
      : [];

  return [
    ...directTimes,
    ...Object.values(record).flatMap((entry) =>
      extractNamedValueKeyframeTimes(entry)
    ),
  ];
}

function isAnimatedProperty(value: unknown): value is AnimatedProperty {
  return (
    value !== null &&
    typeof value === "object" &&
    hasTimedKeyframeList(value as AnimatedProperty)
  );
}

function formatOtherPropertyName(path: string): string {
  const segments = normalizeKeyframePath(path).split(".").filter(Boolean);
  if (!segments.length) {
    return "Other";
  }

  return segments
    .map((segment) =>
      /^\d+$/.test(segment)
        ? `#${Number(segment) + 1}`
        : formatTextAnimationPresetName(segment)
    )
    .join(" ");
}

function extractUnhandledAnimatedKeyframeInfo(
  value: unknown,
  basePath: string,
  coveredPaths: Set<string>,
  orderRef: { value: number }
): KeyframeInfo[] {
  const normalizedBasePath = normalizeKeyframePath(basePath);
  if (basePath && coveredPaths.has(normalizedBasePath)) {
    return [];
  }

  if (basePath && isAnimatedProperty(value)) {
    const propertyName = formatOtherPropertyName(normalizedBasePath);
    const row = createRowDescriptor(
      `other-${normalizedBasePath}`,
      propertyName,
      orderRef.value
    );
    orderRef.value += 1;

    return extractKeyframeInfo(
      value,
      normalizedBasePath,
      "other",
      propertyName,
      row
    );
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry, index) =>
      extractUnhandledAnimatedKeyframeInfo(
        entry,
        `${basePath}[${index}]`,
        coveredPaths,
        orderRef
      )
    );
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([key, entry]) =>
        extractUnhandledAnimatedKeyframeInfo(
          entry,
          basePath ? `${basePath}.${key}` : key,
          coveredPaths,
          orderRef
        )
    );
  }

  return [];
}

export function getLayerKeyframeInfo(layer: unknown): KeyframeInfo[] {
  const currentLayer = layer as LayerWithKeyframes;
  const keyframes: KeyframeInfo[] = [];

  keyframes.push(
    ...extractTransformKeyframeInfo(currentLayer.ks, "ks", {
      position: 0,
      scale: 1,
      rotation: 2,
      opacity: 3,
      anchor: 4,
      skew: 5,
      skewAxis: 6,
    })
  );

  if (currentLayer.shapes) {
    currentLayer.shapes.forEach((shape, shapeIndex) => {
      const shapeOrder = 100 + shapeIndex;
      const shapeRow = createRowDescriptor("shape", "Shape", shapeOrder);

      keyframes.push(
        ...extractTransformKeyframeInfo(shape.ks, `shapes[${shapeIndex}].ks`, {}).map(
          (keyframe) => ({
            ...keyframe,
            propertyType: "shape" as KeyframePropertyType,
            propertyName: `Shape ${shapeIndex + 1} ${keyframe.propertyName}`,
            rowId: shapeRow.id,
            rowName: shapeRow.name,
            rowOrder: shapeRow.order,
          })
        )
      );

      shape.it?.forEach((item, itemIndex) => {
        keyframes.push(
          ...extractTransformKeyframeInfo(
            item.ks,
            `shapes[${shapeIndex}].it[${itemIndex}].ks`,
            {}
          ).map((keyframe) => ({
            ...keyframe,
            propertyType: "shape" as KeyframePropertyType,
            propertyName: `Shape ${shapeIndex + 1}.${itemIndex + 1} ${keyframe.propertyName}`,
            rowId: shapeRow.id,
            rowName: shapeRow.name,
            rowOrder: shapeRow.order,
          }))
        );
      });
    });

    keyframes.push(
      ...extractShapeStyleKeyframeInfo(currentLayer.shapes, "shapes", {
        value: 160,
      })
    );
  }

  if (currentLayer.t) {
    currentLayer.t.a?.forEach((animator, animatorIndex) => {
      keyframes.push(
        ...extractTextAnimatorKeyframeInfo(animator, animatorIndex, 210 + animatorIndex)
      );
    });
  }

  keyframes.push(
    ...extractNamedValueKeyframeInfo(currentLayer.ef, "ef", { value: 320 })
  );

  const coveredPaths = new Set([
    ...keyframes.map((keyframe) => getKeyframePropertyPath(keyframe.path)),
    TEXT_DOCUMENT_PROPERTY_PATH,
  ]);
  const otherKeyframeOrder = { value: 500 };
  keyframes.push(
    ...extractUnhandledAnimatedKeyframeInfo(
      currentLayer,
      "",
      coveredPaths,
      otherKeyframeOrder
    )
  );

  return keyframes.sort((left, right) => {
    if (left.t !== right.t) {
      return left.t - right.t;
    }

    if (left.rowOrder !== right.rowOrder) {
      return left.rowOrder - right.rowOrder;
    }

    return left.keyframeIndex - right.keyframeIndex;
  });
}

export function getLayerKeyframeTimes(layer: unknown): number[] {
  const currentLayer = layer as LayerWithKeyframes;
  const times: number[] = [];

  times.push(...extractTransformKeyframeTimes(currentLayer.ks));

  if (currentLayer.shapes) {
    currentLayer.shapes.forEach((shape) => {
      times.push(...extractTransformKeyframeTimes(shape.ks));
      shape.it?.forEach((item) => {
        times.push(...extractTransformKeyframeTimes(item.ks));
      });
    });
    times.push(...extractShapeStyleKeyframeTimes(currentLayer.shapes));
  }

  if (currentLayer.t) {
    currentLayer.t.a?.forEach((animator) => {
      times.push(...extractTextAnimatorKeyframeTimes(animator));
    });
  }

  times.push(...extractNamedValueKeyframeTimes(currentLayer.ef));

  return Array.from(new Set(times)).sort((left, right) => left - right);
}

export function getAbsoluteKeyframeTimes(layer: unknown): number[] {
  return getLayerKeyframeTimes(layer);
}

export function getUniquePropertyTypes(keyframes: KeyframeInfo[]): KeyframePropertyType[] {
  const types = new Set<KeyframePropertyType>();
  keyframes.forEach((keyframe) => {
    types.add(keyframe.propertyType);
  });

  return DISPLAY_PROPERTY_TYPES.filter((type) => types.has(type));
}

export function groupKeyframesByPropertyType(
  keyframes: KeyframeInfo[]
): Map<KeyframePropertyType, KeyframeInfo[]> {
  const map = new Map<KeyframePropertyType, KeyframeInfo[]>();

  keyframes.forEach((keyframe) => {
    const group = map.get(keyframe.propertyType) ?? [];
    group.push(keyframe);
    map.set(keyframe.propertyType, group);
  });

  return map;
}

export function getKeyframeRows(keyframes: KeyframeInfo[]): KeyframeRow[] {
  const rows = new Map<string, KeyframeRow>();

  keyframes.forEach((keyframe) => {
    const existing = rows.get(keyframe.rowId);
    if (existing) {
      existing.keyframes.push(keyframe);
      return;
    }

    rows.set(keyframe.rowId, {
      id: keyframe.rowId,
      propertyType: keyframe.propertyType,
      propertyName: keyframe.rowName,
      keyframes: [keyframe],
      textAnimation: keyframe.textAnimation,
      order: keyframe.rowOrder,
    });
  });

  return Array.from(rows.values()).sort((left, right) => left.order - right.order);
}

export function calculateTrackHeight(rowCount: number): number {
  if (rowCount === 0) {
    return TRACK_HEIGHT_CONFIG.BASE_HEIGHT;
  }

  const contentHeight =
    TRACK_HEIGHT_CONFIG.LAYER_BAR_HEIGHT +
    rowCount * TRACK_HEIGHT_CONFIG.KEYFRAME_ROW_HEIGHT;

  return Math.max(TRACK_HEIGHT_CONFIG.BASE_HEIGHT, contentHeight);
}
